import { useEffect, useRef, type PointerEvent as ReactPointerEvent } from 'react';
import { scorePracticalExam, type PracticalExamSheetInput } from '@lms/shared-types';
import { ExamStepHeader } from '@/features/practical-exams/PracticalExamStudentStep';

const PHASE_COPY = {
  'km-end': {
    kicker: 'Paso 5 de 7',
    title: 'Kilometraje final',
    text: 'Ingrese el kilometraje del vehículo al terminar el examen.',
    back: 'Volver a la ficha',
    continue: 'Continuar al Paso 6: Firma del alumno',
  },
  'student-sign': {
    kicker: 'Paso 6 de 7',
    title: 'Firma del alumno',
    text: 'El postulante firma dentro del recuadro.',
    back: 'Volver al kilometraje',
    continue: 'Continuar',
  },
  'examiner-sign': {
    kicker: 'Paso 7 de 7',
    title: 'Firma del instructor',
    text: 'El examinador firma para cerrar la ficha.',
    back: 'Volver a la firma del alumno',
    continue: 'Ver ficha',
  },
} as const;

export type PracticalExamClosePhase = keyof typeof PHASE_COPY;

function paintSignature(canvas: HTMLCanvasElement, dataUrl: string) {
  const rect = canvas.getBoundingClientRect();
  const width = rect.width || 640;
  const height = rect.height || 180;
  const ratio = window.devicePixelRatio || 1;
  canvas.width = Math.max(1, Math.floor(width * ratio));
  canvas.height = Math.max(1, Math.floor(height * ratio));
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.strokeStyle = '#1c1b1f';
  ctx.lineWidth = 2.2;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  if (!dataUrl) return;
  const image = new Image();
  image.onload = () => {
    ctx.drawImage(image, 0, 0, width, height);
    ctx.strokeStyle = '#1c1b1f';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };
  image.src = dataUrl;
}

function SignaturePad({
  value,
  disabled,
  label,
  onChange,
}: {
  value: string;
  disabled: boolean;
  label: string;
  onChange: (value: string) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const ink = useRef(Boolean(value));
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const paint = () => paintSignature(canvas, valueRef.current);
    const frame = window.requestAnimationFrame(paint);
    window.addEventListener('resize', paint);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', paint);
    };
  }, []);

  function point(event: ReactPointerEvent<HTMLCanvasElement>) {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  function start(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (disabled) return;
    const ctx = event.currentTarget.getContext('2d');
    if (!ctx) return;
    drawing.current = true;
    ctx.strokeStyle = '#1c1b1f';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    event.currentTarget.setPointerCapture(event.pointerId);
    const next = point(event);
    ctx.fillStyle = '#1c1b1f';
    ctx.beginPath();
    ctx.arc(next.x, next.y, 1.1, 0, Math.PI * 2);
    ctx.fill();
    ink.current = true;
    ctx.beginPath();
    ctx.moveTo(next.x, next.y);
  }

  function move(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (!drawing.current || disabled) return;
    const ctx = event.currentTarget.getContext('2d');
    if (!ctx) return;
    const next = point(event);
    ctx.lineTo(next.x, next.y);
    ctx.stroke();
    ink.current = true;
  }

  function end(event: ReactPointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return;
    drawing.current = false;
    if (!ink.current) return;
    onChange(event.currentTarget.toDataURL('image/png'));
  }

  function clear() {
    const canvas = canvasRef.current;
    if (!canvas || disabled) return;
    ink.current = false;
    paintSignature(canvas, '');
    onChange('');
  }

  return (
    <section className="flex w-full flex-col gap-6 rounded-2xl bg-white p-6 shadow-sm lg:p-8">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-[22px] text-[#080e5c]" aria-hidden="true">draw</span>
          <label className="text-base font-bold text-dash-on-surface" htmlFor="signatureCanvas">
            {label} <span className="text-error-600">*</span>
          </label>
        </div>
        <button
          type="button"
          onClick={clear}
          disabled={disabled || !value}
          title="Reiniciar trazo"
          className="inline-flex items-center gap-1.5 rounded-lg bg-[#eff4ff] px-3 py-1.5 text-[12px] font-semibold tracking-wide text-dash-on-surface-variant uppercase transition-colors hover:bg-red-50 hover:text-error-600 disabled:opacity-40"
        >
          <span className="material-symbols-outlined text-[18px]" aria-hidden="true">delete_sweep</span>
          Limpiar firma
        </button>
      </div>
      <div className="relative flex min-h-[320px] w-full flex-col items-center justify-center overflow-hidden rounded-2xl border border-[#e2e8f0] bg-white p-2 shadow-inner">
        <canvas
          id="signatureCanvas"
          ref={canvasRef}
          aria-label={label}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={end}
          className="h-80 w-full cursor-crosshair touch-none rounded-xl bg-white"
        />
        <div className="pointer-events-none absolute right-12 bottom-12 left-12 flex flex-col items-center">
          <div className="h-0.5 w-full bg-[#c7c5d2]/60" />
          <span className="mt-2 text-[11px] tracking-widest text-dash-on-surface-variant/80 uppercase">
            Línea base para la firma
          </span>
        </div>
        <div className="pointer-events-none absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1 text-[13px] text-dash-on-surface-variant shadow-sm backdrop-blur-sm">
          <span className="material-symbols-outlined text-[16px] text-[#384dd0]" aria-hidden="true">touch_app</span>
          <span>Firme con el dedo o el mouse, sin salir del recuadro.</span>
        </div>
      </div>
    </section>
  );
}

export function PracticalExamCloseStep({
  phase,
  sheet,
  disabled = false,
  pending = false,
  onBack,
  onPatch,
  onContinue,
}: {
  phase: PracticalExamClosePhase;
  sheet: PracticalExamSheetInput;
  disabled?: boolean;
  pending?: boolean;
  onBack: () => void;
  onPatch: (partial: Partial<PracticalExamSheetInput>) => void;
  onContinue: () => void;
}) {
  const copy = PHASE_COPY[phase];
  const score = scorePracticalExam(sheet.faults);
  const kmTooLow = sheet.kmStart != null && sheet.kmEnd != null && sheet.kmEnd < sheet.kmStart;
  const kmReady = sheet.kmEnd != null && sheet.kmEnd >= 0 && sheet.kmEnd <= 9_999_999 && !kmTooLow;
  const signature = phase === 'student-sign' ? sheet.studentSignature : sheet.examinerSignature;
  const ready = phase === 'km-end' ? kmReady : Boolean(signature);

  return (
    <div className="lms-site-admin-hub-page font-sans">
      <ExamStepHeader
        title={copy.title}
        text={copy.text}
        step={phase === 'km-end' ? 5 : phase === 'student-sign' ? 6 : 7}
        status={sheet.status === 'final' ? 'final' : 'draft'}
        backLabel={copy.back}
        onBack={onBack}
      />

      {phase === 'km-end' ? (
        <>
          <div className="flex flex-col gap-8 rounded-xl bg-white p-6 shadow-sm md:p-8">
            <div className="flex w-full flex-col justify-between gap-4 rounded-xl bg-[#eff4ff] p-5 sm:flex-row sm:items-center">
              <div className="flex items-center gap-3.5">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#e6eeff] text-[#384dd0]" aria-hidden="true">
                  <span className="material-symbols-outlined text-[22px]">speed</span>
                </span>
                <div>
                  <div className="text-[12px] font-semibold tracking-wider text-dash-on-surface-variant uppercase">Referencia inicial</div>
                  <div className="text-2xl leading-tight font-semibold text-[#080e5c]">
                    {sheet.kmStart != null ? sheet.kmStart.toLocaleString('es-CL') : '—'}{' '}
                    <span className="text-base font-semibold text-dash-on-surface-variant">KM</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 rounded-lg bg-[#e6eeff] px-3 py-1.5 text-[13px] text-dash-on-surface-variant">
                <span className="material-symbols-outlined text-[18px] text-[#384dd0]" aria-hidden="true">check_circle</span>
                <span>Registrado en el paso 3</span>
              </div>
            </div>

            <div className="flex flex-col gap-2.5">
              <label className="flex items-center justify-between text-base font-semibold text-dash-on-surface" htmlFor="finalKmInput">
                <span>
                  Km final <span className="text-error-600">*</span>
                </span>
                <span className="text-[13px] font-normal text-dash-on-surface-variant">Obligatorio</span>
              </label>
              <div className="relative flex items-center">
                <span className="pointer-events-none absolute left-4 flex items-center text-dash-on-surface-variant" aria-hidden="true">
                  <span className="material-symbols-outlined text-[22px]">pin</span>
                </span>
                <input
                  id="finalKmInput"
                  name="km_final"
                  type="text"
                  inputMode="numeric"
                  autoFocus
                  value={sheet.kmEnd ?? ''}
                  disabled={disabled}
                  placeholder="Ej. 45248"
                  onChange={(event) => {
                    const digits = event.target.value.replace(/\D/g, '').slice(0, 7);
                    onPatch({ kmEnd: digits === '' ? null : Number(digits) });
                  }}
                  className="h-14 w-full rounded-lg border border-[#e2e8f0] bg-white pr-16 pl-12 text-2xl font-semibold text-dash-on-surface shadow-sm outline-none transition-all placeholder:text-base placeholder:font-normal placeholder:text-dash-on-surface-variant/50 focus:border-[#384dd0] focus:ring-2 focus:ring-[#384dd0]/30 disabled:opacity-60"
                />
                <span className="pointer-events-none absolute right-4 rounded bg-[#eff4ff] px-2 py-1 text-[12px] font-bold tracking-wider text-dash-on-surface-variant uppercase select-none" aria-hidden="true">
                  Km
                </span>
              </div>
              <div className="mt-1 flex items-center gap-1.5 text-[13px] text-dash-on-surface-variant">
                <span className="material-symbols-outlined text-[16px]" aria-hidden="true">info</span>
                <span>Escriba el número entero, sin puntos ni comas.</span>
              </div>
              {kmTooLow ? (
                <p className="text-sm font-medium text-error-600" role="alert">
                  El km final no puede ser menor que el km inicial.
                </p>
              ) : null}
            </div>
          </div>

          <div className="mt-6 flex flex-col items-center justify-end gap-4 rounded-xl bg-white p-4 shadow-sm sm:flex-row">
            <button
              type="button"
              disabled={disabled || pending || !ready}
              onClick={onContinue}
              className="order-1 flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-[#080e5c] px-6 text-base font-semibold text-white shadow-sm transition-opacity hover:opacity-95 disabled:opacity-60 sm:order-2 sm:w-auto"
            >
              {pending ? 'Guardando…' : copy.continue}
              <span className="material-symbols-outlined text-[18px]" aria-hidden="true">arrow_forward</span>
            </button>
          </div>
        </>
      ) : (
        <div className="flex flex-col gap-6">
          <div className="flex flex-wrap items-center gap-2 rounded-xl bg-[#eff4ff] px-4 py-3 text-dash-on-surface">
            <span
              className={`material-symbols-outlined text-[20px] ${score.passed ? 'text-emerald-800' : 'text-error-600'}`}
              aria-hidden="true"
            >
              {score.passed ? 'check_circle' : 'cancel'}
            </span>
            <span className="font-medium">Resultado:</span>
            <span className={`font-bold ${score.passed ? 'text-emerald-800' : 'text-error-600'}`}>
              {score.passed ? 'Aprobado' : 'Reprobado'}
            </span>
            <span className="text-dash-on-surface-variant">·</span>
            <span className="font-medium text-dash-on-surface-variant">
              {score.mild} leves, {score.serious} graves, {score.failing} reprobatorias.
            </span>
          </div>

          <SignaturePad
            key={phase}
            value={signature}
            disabled={disabled || pending}
            label={phase === 'student-sign' ? 'Firma del alumno' : 'Firma del instructor'}
            onChange={(next) =>
              onPatch(phase === 'student-sign' ? { studentSignature: next } : { examinerSignature: next })
            }
          />

          <nav className="flex w-full flex-col items-center justify-end gap-4 rounded-2xl bg-white p-4 shadow-sm sm:flex-row">
            <button
              type="button"
              disabled={disabled || pending || !ready}
              onClick={onContinue}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#080e5c] px-8 py-3.5 text-base font-bold text-white shadow-md transition-colors hover:bg-[#080e5c]/90 disabled:opacity-60 sm:w-auto"
            >
              {pending ? 'Guardando…' : copy.continue}
              <span className="material-symbols-outlined text-[20px]" aria-hidden="true">arrow_forward</span>
            </button>
          </nav>
        </div>
      )}
    </div>
  );
}
